import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Alert from "../../utils/Alert";
import Card from "../../utils/Card";
import Collapse from "../../utils/Collapse";
import Button from "../../buttons/Button";
import { useTranslation } from "react-i18next";
import useApi from "../../../hooks/useApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarehouse, faUsers } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-duotone-svg-icons";
import {
  getAllModules,
  getActiveModules,
  getExpiredModules,
  regenerateApiKey,
  purchaseModule,
  getUserApiUsageHistory,
} from "../../../services/apiService";
import { formatDate } from "../../../utils/formatting";
import { updateApiKey } from "../../../redux/slices/userSlice";
import { toast } from "react-toastify";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";

export const RestaurantAPI = ({ tKey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [modules, setModules] = useState([]);
  const [activeModules, setActiveModules] = useState([]);
  const [expiredModules, setExpiredModules] = useState([]);
  const [history, setHistory] = useState([]);
  const userId = useSelector((state) => state.user.userId);
  const modulesIcons = [
    { label: "operations", icon: faWarehouse },
    { label: "customers", icon: faUsers },
  ];
  const apiKey = useSelector((state) => state.user.apiKey);
  const nbRestaurants = useSelector((state) => state.user.restaurants.length);

  const { error: modulesError, request: getAllModulesRequest } =
    useApi(getAllModules);
  const { request: getActiveModulesRequest } = useApi(getActiveModules);
  const { request: getExpiredModulesRequest } = useApi(getExpiredModules);
  const { request: regenerateApiKeyRequest } = useApi(regenerateApiKey);
  const { request: purchaseModuleRequest } = useApi(purchaseModule);
  const { request: getApiUsageHistoryRequest } = useApi(getUserApiUsageHistory);

  const loadModules = useCallback(() => {
    getAllModulesRequest().then((response) => {
      setModules(response.data || []);
    });
    getActiveModulesRequest(userId).then((response) => {
      setActiveModules(response.data || []);
    });
    getExpiredModulesRequest(userId).then((response) => {
      setExpiredModules(response.data || []);
    });
    getApiUsageHistoryRequest(userId).then((response) => {
      setHistory(response.data || []);
    });
  }, [
    getAllModulesRequest,
    getActiveModulesRequest,
    getExpiredModulesRequest,
    getApiUsageHistoryRequest,
    userId,
  ]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const handleRegenerateApiKey = useCallback(() => {
    regenerateApiKeyRequest(userId)
      .then((response) => {
        dispatch(updateApiKey(response.data.apiKey));
      })
      .catch((error) => {
        console.error("Error regenerating API key:", error);
      });
  }, [regenerateApiKeyRequest, dispatch, userId]);

  const handlePurchaseModule = useCallback(
    (planId) => {
      purchaseModuleRequest(userId, planId)
        .then(() => {
          loadModules();
          toast.success(t(`${tKey}purchase.success`));
        })
        .catch((error) => {
          console.error("Error purchasing module:", error);
          toast.error(t(`${tKey}purchase.error`));
        });
    },
    [t, tKey, purchaseModuleRequest, loadModules, userId]
  );

  return (
    <div className="dashboard-content">
      <Alert variant="pastel-info">
        <div dangerouslySetInnerHTML={{ __html: t(`${tKey}info`) }} />
        <div className="flex align-center justify-between mt-2 gap-1">
          <span className="bold">{t(`${tKey}apiKey`)}</span>
          <span className="bg-white p-2 rounded text-dark">
            {apiKey || "No API Key"}
          </span>
          {!!apiKey && (
            <Button
              size="xs"
              variant="white"
              onClick={() => {
                navigator.clipboard.writeText(apiKey || "No API Key");
                toast.success(t("utils.common.copiedToClipboard"));
              }}
            >
              <FontAwesomeIcon icon={faCopy} className="mr-1" />
              {t("utils.common.copy")}
            </Button>
          )}
          <Button
            className="ml-auto"
            size="xs"
            variant="primary"
            onClick={() => handleRegenerateApiKey()}
          >
            {t(`${tKey}regenerate`)}
          </Button>
        </div>
      </Alert>
      <div className="flex gap-1 mt-3 flex-wrap">
        {modules.map((module, i) => (
          <Card key={module.label} className="module-card">
            <div className="flex align-center justify-between w-100 mb-2">
              <h3 className="m-0">{t(`${tKey}${module.label}.title`)}</h3>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  background: "#f5f5f5",
                  width: 48,
                  height: 48,
                }}
              >
                {!!modulesIcons[i] && (
                  <FontAwesomeIcon
                    icon={modulesIcons[i].icon}
                    className="text-danger"
                    size="lg"
                  />
                )}
              </span>
            </div>
            <span>{t(`${tKey}${module.label}.description`)}</span>
            <div className="flex column gap-1 my-2">
              {[...Array(4)].map((_, i) => (
                <div className="flex align-center" key={i}>
                  <FontAwesomeIcon icon={faCheckCircle} color="green" />
                  <span className="ml-2">
                    {t(`${tKey}${module.label}.feature${i + 1}`)}
                  </span>
                </div>
              ))}
            </div>
            {activeModules.some((m) => m.plan.module.label === module.label) ? (
              <div className="mt-auto">
                <Button key={module.moduleId} size="xs" notClickable>
                  {t(`${tKey}activeTill`, {
                    date: formatDate(
                      activeModules.find(
                        (m) => m.plan.module.label === module.label
                      ).validTo,
                      false
                    ),
                  })}
                </Button>
              </div>
            ) : (
              <div className="flex gap-1 mt-auto">
                {module.plans.map((plan) => (
                  <Button
                    key={plan.planId}
                    size="xs"
                    variant={plan.interval === "month" ? "light" : "info"}
                    onClick={() => handlePurchaseModule(plan.planId)}
                  >
                    {t(`utils.plans.${plan.interval}`)}
                    <span className="bold ml-1">
                      {(plan.priceCents * nbRestaurants) / 100} CHF
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
      <div className="mt-3">
        <Collapse className="bg-white" title={t(`${tKey}usageHistory.label`)}>
          {history.length > 0 ? (
            <div className="table-console p3">
              <table className="w-100">
                <tbody>
                  {history.map((entry, index) => {
                    const isSuccess =
                      !isNaN(entry.status) &&
                      entry.status >= 200 &&
                      entry.status < 300;
                    return (
                      <tr key={index}>
                        <td>
                          <span>{formatDate(entry.timestamp, true)}</span>
                        </td>
                        <td>
                          <span>
                            {
                              modules.find((m) => m.moduleId === entry.moduleId)
                                ?.label
                            }
                          </span>
                        </td>
                        <td>
                          <span>{entry.endpoint}</span>
                        </td>
                        <td>
                          <span>{entry.method}</span>
                        </td>
                        <td
                          className={`bold ${
                            isSuccess ? "text-success" : "text-danger"
                          }`}
                        >
                          {entry.status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Alert variant="info">{t(`${tKey}usageHistory.noData`)}</Alert>
          )}
        </Collapse>
      </div>
      {expiredModules.length > 0 && <span>/</span>}
      {modulesError && (
        <Alert variant="error">
          {t(`${tKey}modulesError`)}: {modulesError.message}
        </Alert>
      )}
    </div>
  );
};

export default RestaurantAPI;
